import { Text } from '@/components/ui/text';
import { useAuth } from '@/hooks/use-auth';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router, usePathname } from 'expo-router';
import { Pressable, View } from 'react-native';
import { FlavrlyLogo } from '../icons/flavrly-logo';
import { TopBarNavLink } from './top-bar-nav-link';
import { TopBarProfileMenu } from './top-bar-profile-menu';
import { TopBarSearch } from './top-bar-search';

interface DesktopTopBarProps {
  unreadCount?: number;
  onCheckIn: () => void;
}

export function DesktopTopBar({ unreadCount, onCheckIn }: DesktopTopBarProps) {
  const theme = useAppTheme();
  const pathname = usePathname();
  const { user: authUser } = useAuth();

  const isActive = (route: string) => {
    if (route === '/activity') {
      return pathname === '/' || pathname === '/activity' || pathname.startsWith('/(tabs)/activity');
    }
    return pathname === route || pathname.startsWith(`/(tabs)${route}`) || pathname.startsWith(route);
  };

  return (
    <View
      className="bg-background border-b border-border"
      style={{
        position: 'sticky' as any,
        top: 0,
        zIndex: 50,
        width: '100%',
        height: 75,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          alignSelf: 'center',
          paddingHorizontal: 24,
        }}
      >
        <Pressable onPress={() => router.push('/(tabs)/activity')}>
          <FlavrlyLogo width={100} showCaption={false} captionColor={theme.colors.primary} />
        </Pressable>

        <View style={{ flexDirection: 'row', alignItems: 'center', height: '100%', marginLeft: 24, gap: 4 }}>
          <TopBarNavLink
            label="Activity"
            isActive={isActive('/activity')}
            onPress={() => router.push('/(tabs)/activity')}
          />
          <TopBarNavLink
            label="Discover"
            isActive={isActive('/discover')}
            onPress={() => router.push('/(tabs)/discover')}
          />
          <TopBarNavLink
            label="Lists"
            isActive={isActive('/lists')}
            onPress={() => router.push('/(root)/lists')}
          />
          <TopBarNavLink
            label="Leaderboards"
            isActive={isActive('/leaderboard')}
            onPress={() => router.push('/(root)/leaderboard')}
          />
        </View>

        <View style={{ flex: 1 }} />

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <TopBarSearch />

          <Pressable
            onPress={onCheckIn}
            className="bg-primary"
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: 5,
              paddingHorizontal: 16,
              paddingVertical: 8,
              gap: 4,
            }}
            accessibilityLabel="Check in"
            accessibilityRole="button"
          >
            <Ionicons name="add" size={18} className="text-background" />
            <Text className="text-background" style={{ fontSize: 14, fontWeight: '600' }}>
              Check in
            </Text>
          </Pressable>

          <Pressable
            onPress={() => router.push('/(tabs)/notifications')}
            style={{ position: 'relative', padding: 6 }}
            accessibilityLabel="Notifications"
            accessibilityRole="button"
          >
            <Ionicons
              name={isActive('/notifications') ? 'notifications' : 'notifications-outline'}
              size={22}
              color={isActive('/notifications') ? theme.colors.text : theme.colors.textSecondary}
            />
            {unreadCount !== undefined && unreadCount > 0 && (
              <View
                className="bg-primary"
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  minWidth: 16,
                  height: 16,
                  borderRadius: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingHorizontal: 4,
                }}
              >
                <Text className="text-background" style={{ fontSize: 10, fontWeight: '700' }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Text>
              </View>
            )}
          </Pressable>

          <TopBarProfileMenu />
        </View>
      </View>
    </View>
  );
}
